import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { InfoBar } from "src/library/components/InfoBar";
import {
  ISupportTicket,
  SupportTicketPriority
} from "src/modules/support/api/support.api";
import { formatUserName } from "src/core/users/utils/format-user-name";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import {
  IUser,
  IUserGroup
} from "src/core/users/api/users.api";
import SupportPriorityDropdown from "src/modules/support/components/SupportPriorityDropdown";
import AssignUserAndGroupSelect from "src/core/users/components/AssignUserAndGroupSelect";

import SupportTicketStatusStyled from "./SupportTicketStatus";
import SupportTicketOriginStyled from "./SupportTicketOrigin";
import { TotalSupportTicketTimeTaken } from "./SupportTicketTimeTaken";

export type SupportTicketSidebarUpdate = "userGroupId" | "assignedUserId" | "priority";

interface IProps {
  isLoading: boolean;
  data: ISupportTicket | null;
  terminology: ITerminology;
  displayAdminContent: boolean;
  handleUpdate: (updateType: SupportTicketSidebarUpdate, updateValue: number | SupportTicketPriority | null) => void;
}

const SupportTicketSidebar: React.ReactFCWithChildren<IProps> = ({
  isLoading, data, terminology, displayAdminContent, handleUpdate
}) => {
  const [ selectedUserGroup, setSelectedUserGroup ] = useState<IUserGroup | null>(null);
  const [ assignedUser, setAssignedUser ] = useState<IUser | null>(null);

  // keep local state aligned with component consumer
  useEffect(() => {
    if (data?.userGroup || data?.userGroupId === null) {
      setSelectedUserGroup(data.userGroup || null);
    }
  }, [ data?.userGroupId, data?.userGroup ]);

  // keep local state aligned with component consumer
  useEffect(() => {
    if (data?.assignedUser || data?.assignedUserId === null) {
      setAssignedUser(data.assignedUser || null);
    }
  }, [ data?.assignedUser, data?.assignedUserId ]);

  const supportTicketInfoItems = useMemo(() => {
    const items = [
      {
        label: "Status",
        value: data ? <SupportTicketStatusStyled status={data.status} /> : "-"
      },
      {
        label: "Reported by",
        value: data?.user ? formatUserName(data.user) : "-"
      },
      {
        label: `${terminology.drop.singular.text} name`,
        value: data?.branch ? formatDropName(data.branch) : "-"
      }
    ];

    if (displayAdminContent) {
      items.push({
        label: "Origin",
        value: data?.origin ? <SupportTicketOriginStyled origin={data.origin} /> : "-"
      });

      // Priority level selection dropdown
      items.push({
        label: "Priority",
        value: <SupportPriorityDropdown
          priority={data?.priority}
          handleUpdate={(priority: SupportTicketPriority) => {
            handleUpdate("priority", priority);
          }}
               />
      });

      items.push({
        label: "Total Time Taken",
        value: data?.totalTimeTaken && data?.timeTaken ? (
          <TotalSupportTicketTimeTaken
            minutes={data.totalTimeTaken}
            timeTaken={data.timeTaken}
          />
        ) : "-"
      });
    }

    return items;
  }, [
    data,
    displayAdminContent,
    handleUpdate,
    terminology.drop.singular.text
  ]);

  return (
    <InfoBar
      disableSticky
      isLoading={isLoading}
      title="Ticket details"
      primaryAction={null}
      secondaryAction={null}
      items={supportTicketInfoItems}
    >
      {displayAdminContent && (
        <AssignUserAndGroupSelect
          selectedUserGroup={selectedUserGroup ?? null}
          assignedUser={assignedUser ?? null}
          terminology={terminology}
          handleUpdate={handleUpdate}
        />
      )}
    </InfoBar>
  );
};

export default SupportTicketSidebar;
